import { Clock, ArrowRight } from "lucide-react";
import { Button } from "@repo/ui";

interface TrialBannerProps {
  pharmacy: {
    subscription_status: string;
    trial_ends_at: string | null;
  };
  onViewPlans: () => void;
}

// Must stay in sync with TRIAL_GRACE_DAYS in
// apps/api/src/modules/admin/pharmacies/trial.constants.ts
const TRIAL_GRACE_DAYS = 3;

function getDaysRemaining(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
}

function formatLockoutDate(dateStr: string): string {
  const trialEnds = new Date(dateStr);
  const lockout = new Date(trialEnds);
  lockout.setDate(lockout.getDate() + TRIAL_GRACE_DAYS);
  return lockout.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export function TrialBanner({ pharmacy, onViewPlans }: TrialBannerProps) {
  if (pharmacy.subscription_status !== "trial") return null;

  const daysRemaining = getDaysRemaining(pharmacy.trial_ends_at);
  if (daysRemaining === null) return null;

  const isInGrace = daysRemaining < 0;
  const isUrgent = isInGrace || daysRemaining <= 3;

  let title: string;
  let description: string;

  if (isInGrace) {
    const lockoutDate = formatLockoutDate(pharmacy.trial_ends_at!);
    title = `Trial ended — subscribe by ${lockoutDate}`;
    description =
      "You still have full access during the 3-day grace period. Subscribe to keep your data and avoid interruption.";
  } else if (daysRemaining === 0) {
    title = "Your free trial ends today";
    description =
      "Subscribe now to keep access to all your compliance data and reports.";
  } else {
    title = `Your free trial ends in ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""}`;
    description =
      "Subscribe now to keep access to all your compliance data and reports.";
  }

  return (
    <div
      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border px-5 py-4 ${
        isUrgent
          ? "border-destructive/30 bg-destructive/5"
          : "border-primary/20 bg-primary-soft"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            isUrgent ? "bg-destructive/10" : "bg-primary/10"
          }`}
        >
          <Clock
            className={`h-4.5 w-4.5 ${isUrgent ? "text-destructive" : "text-primary"}`}
          />
        </div>
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Button
        onClick={onViewPlans}
        size="sm"
        variant={isUrgent ? "destructive" : "default"}
        className="shrink-0"
      >
        View Plans
        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
